import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

const StickyWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 120;
`;

const Nav = styled.header`
  background: ${({ theme }) => theme.colors.navBackground};
  color: ${({ theme }) => theme.colors.navText};
  backdrop-filter: blur(12px);
  padding: 0.75rem 2.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.navBorder};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.75rem 1.5rem;
  }
`;

const NavContainer = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.navText};
  text-decoration: none;
`;

const LogoWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  height: 70px;
  width: auto;
  object-fit: contain;
  object-position: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 60px;
  }
`;

const Wordmark = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;

const WordmarkTitle = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: 1.6rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

const WordmarkSubtitle = styled.span`
  font-size: 0.85rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.highlight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const NavLinks = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  display: flex;
  gap: 1.75rem;
  align-items: center;
  transition: transform 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 1.25rem 1.5rem 2rem;
    background: ${({ theme }) => theme.colors.navBackground};
    border-bottom: 2px solid ${({ theme }) => theme.colors.navBorder};
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-8px)')};
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  }
`;

const NavItem = styled.li``;

const baseLinkStyles = css`
  color: ${({ theme }) => theme.colors.navText};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.85rem;
  position: relative;
  padding-bottom: 0.25rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const AnchorButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  ${baseLinkStyles};
`;

const RouteLink = styled(Link)<{ $active: boolean }>`
  ${baseLinkStyles};

  &::after {
    width: ${({ $active }) => ($active ? '100%' : '0')};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: ${({ theme }) => theme.colors.navText};
  font-size: 1.8rem;
  padding: 0.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: block;
  }
`;

const flash = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const SubNavBar = styled.div`
  background: ${({ theme }) => theme.colors.navBackground};
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 0.5rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.navBorder};
`;

const SubNavButton = styled(Link)`
  background: ${({ theme }) => theme.colors.accent};
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 8px 24px;
  border-radius: 4px;
  text-decoration: none;
  transition: background 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight};
  }
`;

const SubNavAnchor = styled.a`
  background: ${({ theme }) => theme.colors.accent};
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 8px 24px;
  border-radius: 4px;
  text-decoration: none;
  transition: background 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight};
  }
`;

const FlashingButton = styled(SubNavButton)`
  animation: ${flash} 1.5s ease-in-out infinite;
`;

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { label: 'Welcome', type: 'anchor' as const, target: 'welcome' },
    { label: 'About', type: 'anchor' as const, target: 'about' },
    { label: 'Support', type: 'anchor' as const, target: 'offerings' },
    { label: 'Meetings', type: 'anchor' as const, target: 'meetings' },
    { label: 'Resources', type: 'anchor' as const, target: 'resources' },
    { label: 'Newsletter', type: 'route' as const, path: '/newsletter' },
    { label: 'Videos', type: 'anchor' as const, target: 'videos' },
    { label: 'Feeds', type: 'anchor' as const, target: 'feeds' },
    { label: 'Contact', type: 'anchor' as const, target: 'contact' },
    { label: 'Settings', type: 'route' as const, path: '/settings' },
  ];

  const handleAnchorSelect = (target: string) => {
    setIsMenuOpen(false);

    const scrollToSection = () => {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: target } });
    } else {
      scrollToSection();
    }
  };

  return (
    <StickyWrapper>
    <Nav>
      <NavContainer>
        <BrandLink to="/">
          <LogoWrapper>
            <LogoImage src="/TMW-Logo-Flatten.jpg" alt="The Men's Well logo" />
          </LogoWrapper>
        </BrandLink>

        <MobileMenuButton
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          ☰
        </MobileMenuButton>

        <NavLinks isOpen={isMenuOpen}>
          {navItems.map((item) => (
            <NavItem key={item.label}>
              {item.type === 'route' ? (
                <RouteLink
                  to={item.path}
                  $active={location.pathname === item.path}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </RouteLink>
              ) : (
                <AnchorButton onClick={() => handleAnchorSelect(item.target)}>
                  {item.label}
                </AnchorButton>
              )}
            </NavItem>
          ))}
        </NavLinks>
      </NavContainer>
    </Nav>
    <SubNavBar>
      <FlashingButton to="/news-events">News & Events</FlashingButton>
      <SubNavAnchor href={`${process.env.PUBLIC_URL}/The-Mens-Well-Charter.pdf`} target="_blank" rel="noopener noreferrer">Our Charter</SubNavAnchor>
    </SubNavBar>
    </StickyWrapper>
  );
};

export default Navigation;
